import { computed, getCurrentInstance, onBeforeUnmount, ref } from 'vue';
import { ThirdPartyAuthType, ThirdPartySignupParameters } from 'types/third-party-auth';
import { captureThirdPartyUser } from '@api/lead-capture';
import { defineStore } from 'pinia';
import { determineLoginRoute } from '@utils/login-redirects';
import { getItemFromLocalStorage } from '@utils/web-storage';
import { RouteLocationNamedRaw } from 'vue-router';
import { useAppStore } from '@stores/app';
import { useUserStore } from '@stores/user';

export const useThirdPartyAuthStore = defineStore('thirdPartyAuth', () => {
	const _isPageDisabled = ref(false);
	const isAppleLibraryLoaded = ref(false);
	const isGoogleLibraryLoaded = ref(false);

	const leadCaptured = computed((): boolean => {
		return !!getItemFromLocalStorage('emailcapture');
	});

	const isLoginEnabled = computed((): boolean => {
		return !_isPageDisabled.value;
	});

	const isSignupEnabled = computed((): boolean => {
		const appStore = useAppStore();
		return !_isPageDisabled.value && !leadCaptured.value && !appStore.isMobileWebview;
	});

	async function signUp(type: ThirdPartyAuthType, payload: ThirdPartySignupParameters): Promise<string> {
		const userResponseData = await captureThirdPartyUser(type, payload);
		const appStore = useAppStore();
		const userStore = useUserStore();
		await appStore.setUserAuthData(userResponseData.accessToken);

		let routeName: string;
		if (userResponseData.isNewUser) {
			await userStore.setThirdPartyUser(userResponseData);
			routeName = 'checkout-signup-success-interstitial';
		} else {
			const redirect = (await determineLoginRoute()) as RouteLocationNamedRaw;
			routeName = redirect.name as string;
		}

		return routeName;
	}

	function disable(reset: typeof onBeforeUnmount): void {
		if (!getCurrentInstance()) return;

		_isPageDisabled.value = true;
		reset(() => (_isPageDisabled.value = false));
	}

	function $reset(): void {
		_isPageDisabled.value = false;
		isAppleLibraryLoaded.value = false;
		isGoogleLibraryLoaded.value = false;
	}

	return {
		isAppleLibraryLoaded,
		isGoogleLibraryLoaded,
		isLoginEnabled,
		isSignupEnabled,
		signUp,
		disable,
		$reset
	};
});
